<template>
  <div>
    <CustomModal
      ref="modalRef"
      modalType="info"
      :title="modalTitle"
      :requireAction="true"
      :button-labels="['Confirmar', 'Cancelar']"
      @confirm="handlePrimaryConfirm"
      @cancel="handleCancel"
      :loading="isLoading"
    >
      <template #action="{ setValid, appendTo }">
        <div v-if="modalMode === 'ingreso'" class="calabozo-modal-form">
          <div class="field">
            <label class="calabozo-modal-label secondaryFont">
              DNI
              <span class="calabozo-modal-required">*</span>
            </label>
            <InputText
              v-model="ingresoForm.dni"
              class="w-full custom-focus"
              placeholder="Ingrese DNI"
              :disabled="!isDniEditable"
              @input="setValid(isIngresoFormValid)"
            />
          </div>
          <div class="field">
            <label class="calabozo-modal-label secondaryFont">
              Fecha y hora del ingreso al calabozo
              <span class="calabozo-modal-required">*</span>
            </label>
            <Calendar
              v-model="ingresoForm.fechaHora"
              showTime
              hideOnDateTimeSelect
              hourFormat="24"
              dateFormat="dd/mm/yy"
              :appendTo="appendTo"
              class="w-full custom-focus"
              placeholder="Ingrese fecha y hora"
              @update:modelValue="setValid(isIngresoFormValid)"
            />
          </div>
          <div class="field">
            <label class="calabozo-modal-label">
              Estado de salud visualmente <span class="calabozo-modal-required">*</span>
            </label>
            <InputText
              v-model="ingresoForm.estadoSalud"
              class="w-full custom-focus"
              placeholder="Ingrese estado de salud"
              @input="setValid(isIngresoFormValid)"
            />
          </div>
          <div class="field">
            <label class="calabozo-modal-label">
              Objetos retenidos
              <span class="calabozo-modal-required">*</span>
            </label>
            <InputText
              v-model="ingresoForm.objetosRetenidos"
              class="w-full custom-focus"
              placeholder="Ingrese objetos"
              @input="setValid(isIngresoFormValid)"
            />
          </div>
        </div>

        <div v-else class="calabozo-modal-form">
          <div class="field">
            <label class="calabozo-modal-label">
              Fecha y hora de egreso al calabozo
              <span class="calabozo-modal-required">*</span>
            </label>
            <Calendar
              v-model="egresoForm.fechaHora"
              showTime
              hideOnDateTimeSelect
              hourFormat="24"
              dateFormat="dd/mm/yy"
              :appendTo="appendTo"
              class="w-full custom-focus"
              placeholder="Ingrese fecha y hora"
              @update:modelValue="setValid(isEgresoFormValid)"
            />
          </div>
          <div class="field">
            <label class="calabozo-modal-label">
              Forma de liberación
              <span class="calabozo-modal-required">*</span>
            </label>
            <Dropdown
              v-model="egresoForm.formaLiberacion"
              :options="formaLiberacionOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Seleccione una opción"
              :appendTo="appendTo"
              class="w-full custom-focus"
              @change="setValid(isEgresoFormValid)"
            />
          </div>
          <div class="field">
            <label class="calabozo-modal-label">
              Autoridad que autoriza la salida
              <span class="calabozo-modal-required">*</span>
            </label>
            <Dropdown
              v-model="egresoForm.autoridad"
              :options="autoridadOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Seleccioná la autoridad"
              :appendTo="appendTo"
              class="w-full custom-focus"
              @change="setValid(isEgresoFormValid)"
            />
          </div>
          <div class="field">
            <label class="calabozo-modal-label">Motivos</label>
            <InputText
              v-model="egresoForm.motivos"
              class="w-full custom-focus inputtext"
              placeholder="Ingrese motivos"
              @input="setValid(isEgresoFormValid)"
            />
          </div>
        </div>
      </template>
    </CustomModal>

    <CustomModal
      ref="successModalRef"
      modalType="success"
      :title="successTitle"
      :message="successMessage"
      :requireAction="false"
      :button-labels="['Aceptar']"
      :showSecondaryButton="false"
      @confirm="handleSuccessConfirm"
    />

    <CustomModal
      ref="errorModalRef"
      modalType="error"
      :title="errorTitle"
      :message="errorMessage"
      :requireAction="false"
      :button-labels="['Cerrar']"
      :showSecondaryButton="false"
    />

    <CustomModal
      ref="cancelModalRef"
      modalType="warning"
      title="Cancelar registro"
      message="¿Estás seguro de que querés cancelar el registro del detenido? Los datos cargados hasta el momento no serán guardados."
      :requireAction="false"
      :button-labels="['Cancelar registro', 'Volver']"
      @confirm="handleCancelConfirm"
      @cancel="handleCancelBack"
    />

    <CustomModal
      ref="liberacionConfirmModalRef"
      modalType="warning"
      title="¿Deseás confirmar la liberación del detenido?"
      message="Esta acción registrará la liberación de manera definitiva. Revisá los datos antes de continuar, ya que no se podrá modificar luego."
      :requireAction="false"
      :button-labels="['Continuar', 'Volver']"
      @confirm="handleLiberacionConfirm"
      @cancel="handleLiberacionBack"
    />
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import { useCalabozoStore } from "@/store/Pinia/useCalabozoStore";

const props = defineProps({
  detenido: {
    type: Object,
    required: false,
  },
});

const emit = defineEmits(["close", "success"]);

const calabozoStore = useCalabozoStore();

const modalRef = ref(null);
const successModalRef = ref(null);
const errorModalRef = ref(null);
const cancelModalRef = ref(null);
const isLoading = ref(false);
const liberacionConfirmModalRef = ref(null);

const modalMode = ref("egreso"); // 'ingreso' | 'egreso'

const ingresoForm = reactive({
  fechaHora: null,
  estadoSalud: "",
  objetosRetenidos: "",
  dni: props.detenido ? props.detenido.NumeroIdentificacion : "",
});

const isDniEditable = computed(() => {
  return props.detenido?.NumeroIdentificacion === "Sin Dni Especificado";
});

const egresoForm = reactive({
  fechaHora: null,
  formaLiberacion: null,
  autoridad: null,
  motivos: "",
});

const formaLiberacionOptions = [
  { label: "Por orden judicial", value: "Por orden judicial" },
  { label: "Por orden fiscal", value: "Por orden fiscal" },
  { label: "Cumplimiento de pena", value: "Cumplimiento de pena" },
  { label: "Otra", value: "otra" },
];

const autoridadOptions = [
  { label: "Juez de Garantías", value: "juez" },
  { label: "Fiscal", value: "fiscal" },
  { label: "Jefe de dependencia", value: "jefe_dependencia" },
];

const isIngresoFormValid = computed(
  () =>
    !!ingresoForm.fechaHora &&
    !!ingresoForm.estadoSalud &&
    !!ingresoForm.objetosRetenidos &&
    !!ingresoForm.dni
);

const isEgresoFormValid = computed(
  () => !!egresoForm.fechaHora && !!egresoForm.formaLiberacion && !!egresoForm.autoridad
);

const modalTitle = computed(() =>
  modalMode.value === "ingreso"
    ? "Registrar ingreso del detenido"
    : "Registrar egreso o libertad del detenido"
);

const successTitle = computed(() =>
  modalMode.value === "ingreso" ? "Registro exitoso" : "Liberación registrada con éxito"
);

const successMessage = computed(() =>
  modalMode.value === "ingreso"
    ? "El ingreso del detenido fue registrado correctamente en el sistema. Ya podés continuar con la gestión en Calaboceros."
    : "La liberación del detenido fue registrada correctamente. La información quedó asentada en el sistema."
);

const errorTitle = computed(() =>
  modalMode.value === "ingreso"
    ? "Error al registrar ingreso"
    : "Error al registrar egreso o libertad"
);

const errorMessage = computed(() =>
  modalMode.value === "ingreso"
    ? "Ocurrió un error al registrar el ingreso. Intente nuevamente."
    : "Ocurrió un error al registrar el egreso o libertad. Intente nuevamente."
);

const resetForms = () => {
  ingresoForm.fechaHora = null;
  ingresoForm.estadoSalud = "";
  ingresoForm.objetosRetenidos = "";
  egresoForm.fechaHora = null;
  egresoForm.formaLiberacion = null;
  egresoForm.autoridad = null;
  egresoForm.motivos = "";
};

const open = (mode = "egreso") => {
  modalMode.value = mode;
  resetForms();
  if (modalRef.value) modalRef.value.open();
};

const close = () => {
  if (modalRef.value) modalRef.value.close();
  emit("close");
};

const handleConfirm = async () => {
  if (!props.detenido || !props.detenido.DetencionID) {
    close();
    return;
  }

  try {
    isLoading.value = true;
    if (modalMode.value === "ingreso") {
      await calabozoStore.registrarIngreso(props.detenido.DetencionID, {
        ...ingresoForm,
      });
    } else {
      await calabozoStore.registrarEgreso(props.detenido.DetencionID, { ...egresoForm });
    }

    close();

    if (successModalRef.value) {
      successModalRef.value.open();
    }
  } catch (error) {
    if (errorModalRef.value) {
      errorModalRef.value.open();
    }
  } finally {
    isLoading.value = false;
  }
};

const handlePrimaryConfirm = () => {
  if (modalMode.value === "egreso") {
    if (liberacionConfirmModalRef.value) {
      liberacionConfirmModalRef.value.open();
    }
    return;
  }

  // Para ingreso, se ejecuta directamente el flujo normal
  handleConfirm();
};

const handleCancel = () => {
  if (cancelModalRef.value) {
    cancelModalRef.value.open();
  }
};

const handleCancelBack = () => {
  if (cancelModalRef.value) {
    cancelModalRef.value.close();
  }
};

const handleCancelConfirm = () => {
  if (cancelModalRef.value) {
    cancelModalRef.value.close();
  }
  close();
};

const handleLiberacionBack = () => {
  if (liberacionConfirmModalRef.value) {
    liberacionConfirmModalRef.value.close();
  }
};

const handleLiberacionConfirm = async () => {
  if (liberacionConfirmModalRef.value) {
    liberacionConfirmModalRef.value.close();
  }
  await handleConfirm();
};

const handleSuccessConfirm = () => {
  const payload = modalMode.value === "ingreso" ? { ...ingresoForm } : { ...egresoForm };

  emit("success", { mode: modalMode.value, payload });

  if (successModalRef.value) {
    successModalRef.value.close();
  }
};

watch(
  () => props.detenido,
  (nuevo) => {
    if (!nuevo) {
      close();
    }
  }
);

defineExpose({ open, close });
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");

.calabozo-modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
}

.calabozo-modal-form .field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.calabozo-modal-label {
  font-weight: 500;
  color: #111827;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
  font-family: "Inter", sans-serif;
}

.calabozo-modal-required {
  color: #ef4444;
  margin-left: 2px;
}

::v-deep .p-dropdown .p-dropdown-label {
  border: 1px solid #0b5195;
  color: #0b5195;
  border-radius: 5px;
  border-right: none;
}

::v-deep .p-dropdown-trigger {
  border: 1px solid #0b5195;
  border-left: none;
}

.inputtext {
  border: 1px solid #0b5195;
  border-radius: 5px;
  color: #0b5195;
}

::v-deep .p-calendar .p-component {
  border: 1px solid #0b5195;
  border-radius: 5px;
}
</style>
